import type { MetricRegistryMeta, MetricResult, TileMeta } from '../types';
import metricsRegistry from '../metrics-registry.json';

const registryLabel = (id: string): string =>
  (metricsRegistry as Record<string, { label?: string }>)[id]?.label?.trim() || id;

/** Mock catalog ids + business definitions for MetricInfo / CatalogId badges. */
export const MOCK_REGISTRY_META: Record<string, MetricRegistryMeta> = {
  sales_influenced: {
    id: 'sales_influenced',
    business_definition: 'Count of sales attributed to nurturing in the selected period.',
    catalog: { catalog_id: 'A01' },
  },
  cold_leads_revived: {
    id: 'cold_leads_revived',
    business_definition: 'Count of cold leads revived through nurturing in the selected period.',
    catalog: { catalog_id: 'A02' },
  },
  conversion_rate: {
    id: 'conversion_rate',
    business_definition: 'Share of revived leads that converted to a sale in the selected period.',
    catalog: { catalog_id: 'A03' },
  },
  revived_sales_by_vehicle_model: {
    id: 'revived_sales_by_vehicle_model',
    business_definition: 'Revived sales broken down by vehicle model for the selected period.',
    catalog: { catalog_id: 'A04' },
  },
  revival_prior_crm_status_share: {
    id: 'revival_prior_crm_status_share',
    business_definition: 'CRM status mix when revived customers were first enrolled.',
    catalog: { catalog_id: 'A05' },
  },
  oldest_lead_days_to_revival: {
    id: 'oldest_lead_days_to_revival',
    business_definition: 'MAX(days_to_revival) among period revival sales.',
    catalog: { catalog_id: 'A06' },
  },
  revived_sales_by_content_theme: {
    id: 'revived_sales_by_content_theme',
    business_definition: 'Revived sales attributed to top converting content themes.',
    catalog: { catalog_id: 'A07' },
  },
  revived_sales_from_leads_older_than_90d_share: {
    id: 'revived_sales_from_leads_older_than_90d_share',
    business_definition: 'Share of revived sales from leads older than 90 days at original inquiry.',
    catalog: { catalog_id: 'A08' },
  },
  revived_sales_marked_dead_in_crm_share: {
    id: 'revived_sales_marked_dead_in_crm_share',
    business_definition: 'Share of revived sales that were marked lost, inactive, or bad in CRM at pickup.',
    catalog: { catalog_id: 'A09' },
  },
  revived_sales_no_prior_relationship_share: {
    id: 'revived_sales_no_prior_relationship_share',
    business_definition: 'Share of revived sales from sources with no prior buying relationship.',
    catalog: { catalog_id: 'A10' },
  },
  top_n_nameplate_share: {
    id: 'top_n_nameplate_share',
    business_definition: 'Combined share of top nameplates among revived sales gross.',
    catalog: { catalog_id: 'A11' },
  },
  revived_sales_new_units: {
    id: 'revived_sales_new_units',
    business_definition: 'Count of new units sold from revived leads in the selected period.',
    catalog: { catalog_id: 'A12' },
  },
  revived_sales_used_cpo_units: {
    id: 'revived_sales_used_cpo_units',
    business_definition: 'Count of used/CPO units sold from revived leads in the selected period.',
    catalog: { catalog_id: 'A13' },
  },
  unsold_leads_count: {
    id: 'unsold_leads_count',
    business_definition: 'Operational count of leads that went unsold in the selected period.',
    catalog: { catalog_id: 'B01' },
  },
  lead_capture_missing_email_rate: {
    id: 'lead_capture_missing_email_rate',
    business_definition: 'Percent of leads missing email capture at intake for the selected period.',
    catalog: { catalog_id: 'B02' },
  },
  lead_capture_missing_voi_rate: {
    id: 'lead_capture_missing_voi_rate',
    business_definition: 'Percent of leads missing vehicle-of-interest capture at intake.',
    catalog: { catalog_id: 'B03' },
  },
  internet_conversion_rate: {
    id: 'internet_conversion_rate',
    business_definition: 'Internet lead conversion rate for the selected period.',
    catalog: { catalog_id: 'B04' },
  },
  lead_capture_missing_email_rate_phone: {
    id: 'lead_capture_missing_email_rate_phone',
    business_definition: 'Phone channel email capture gap rate for the selected period.',
    catalog: { catalog_id: 'B05' },
  },
  lead_capture_missing_email_rate_walk_in: {
    id: 'lead_capture_missing_email_rate_walk_in',
    business_definition: 'Walk-in channel email capture gap rate for the selected period.',
    catalog: { catalog_id: 'B06' },
  },
  salesperson_email_capture_rate: {
    id: 'salesperson_email_capture_rate',
    business_definition: 'Per-salesperson email, VOI, and marked-dead performance table.',
    catalog: { catalog_id: 'B07' },
  },
  personalized_touchpoints: {
    id: 'personalized_touchpoints',
    business_definition: 'Count of personalized nurturing touchpoints delivered in the period.',
    catalog: { catalog_id: 'C01' },
  },
  buyer_profile_count: {
    id: 'buyer_profile_count',
    business_definition: 'Active buyer profiles tuned for the dealer in the selected period.',
    catalog: { catalog_id: 'C02' },
  },
  engagement_rate: {
    id: 'engagement_rate',
    business_definition: 'Customer engagement rate across nurturing touchpoints.',
    catalog: { catalog_id: 'C03' },
  },
  unsubscribe_rate: {
    id: 'unsubscribe_rate',
    business_definition: 'Unsubscribe rate for nurturing messages in the selected period.',
    catalog: { catalog_id: 'C04' },
  },
  content_theme_sent_count: {
    id: 'content_theme_sent_count',
    business_definition: 'Content themes sent, engaged, and converted counts for the period.',
    catalog: { catalog_id: 'C05' },
  },
  prospects_under_nurturing: {
    id: 'prospects_under_nurturing',
    business_definition: 'Total prospects currently under nurturing for the dealer.',
    catalog: { catalog_id: 'D01' },
  },
  customers_sit_right_now: {
    id: 'customers_sit_right_now',
    business_definition: 'Section title for current cohort distribution across nurturing states.',
    catalog: { catalog_id: 'D07' },
  },
  cohort_engaged_count: {
    id: 'cohort_engaged_count',
    business_definition: 'Count of customers in the engaged cohort state for the selected period.',
    catalog: { catalog_id: 'D08' },
  },
  cohort_recent_additions_count: {
    id: 'cohort_recent_additions_count',
    business_definition: 'Customers added to the nurturing cohort during the selected period.',
    catalog: { catalog_id: 'D09' },
  },
  cohort_long_term_nurturing_count: {
    id: 'cohort_long_term_nurturing_count',
    business_definition: 'Count of customers in long-term nurturing for the selected period.',
    catalog: { catalog_id: 'D10' },
  },
  cohort_period_removals_count: {
    id: 'cohort_period_removals_count',
    business_definition: 'Customers removed from the nurturing cohort during the selected period.',
    catalog: { catalog_id: 'D11' },
  },
  cohort_unsubscribed_count: {
    id: 'cohort_unsubscribed_count',
    business_definition: 'Count of customers unsubscribed from nurturing in the selected period.',
    catalog: { catalog_id: 'D12' },
  },
  new_enrollments_by_vehicle_model: {
    id: 'new_enrollments_by_vehicle_model',
    business_definition: 'New cold lead enrollments by vehicle model for the selected period.',
    catalog: { catalog_id: 'D13' },
  },
  buyer_segment_count: {
    id: 'buyer_segment_count',
    business_definition: 'Buyer segment performance table for nurturing, revival, and conversion.',
    catalog: { catalog_id: 'D14' },
  },
  attributed_internet_conversion_rate: {
    id: 'attributed_internet_conversion_rate',
    business_definition: 'Attributed internet channel conversion rate for revived pipeline.',
    catalog: { catalog_id: 'D15' },
  },
  walk_in_conversion_rate: {
    id: 'walk_in_conversion_rate',
    business_definition: 'Walk-in channel conversion rate for the selected period.',
    catalog: { catalog_id: 'D16' },
  },
  phone_up_conversion_rate: {
    id: 'phone_up_conversion_rate',
    business_definition: 'Phone-up channel conversion rate for the selected period.',
    catalog: { catalog_id: 'D17' },
  },
  focus_model_nurturing_pool_count: {
    id: 'focus_model_nurturing_pool_count',
    business_definition: 'Nurturing pool count for a focus model in looking ahead.',
    catalog: { catalog_id: 'E01' },
  },
  focus_models_nurturing_pool_share: {
    id: 'focus_models_nurturing_pool_share',
    business_definition: 'Share of nurturing pool for focus models in looking ahead.',
    catalog: { catalog_id: 'E02' },
  },
  model_performance_status: {
    id: 'model_performance_status',
    business_definition: 'Performance status for a focus model in the nurturing pool.',
    catalog: { catalog_id: 'E03' },
  },
  inventory_aged_unit_count: {
    id: 'inventory_aged_unit_count',
    business_definition: 'Aged inventory unit count surfaced for focus model planning.',
    catalog: { catalog_id: 'E04' },
  },
  customer_journey_timeline: {
    id: 'customer_journey_timeline',
    business_definition: 'Narrative timeline of one revival sale from first inquiry through close.',
    catalog: { catalog_id: 'I01' },
  },
  inventory_watchlist: {
    id: 'inventory_watchlist',
    business_definition: 'Inventory and trim items the nurturing team is monitoring this month.',
    catalog: { catalog_id: 'I02' },
  },
  discussion_item_packet: {
    id: 'discussion_item_packet',
    business_definition: 'Numbered discussion items for dealer review from period insights.',
    catalog: { catalog_id: 'I03' },
  },
  monthly_focus_narrative: {
    id: 'monthly_focus_narrative',
    business_definition: 'Narrative summary of monthly focus models and nurturing direction.',
    catalog: { catalog_id: 'I04' },
  },
};

/** Fill any registry entry missing from the map using registry label. */
Object.keys(metricsRegistry).forEach(id => {
  if (MOCK_REGISTRY_META[id]) return;
  MOCK_REGISTRY_META[id] = {
    id,
    business_definition: `${registryLabel(id)} for the selected dealer and period.`,
    catalog: { catalog_id: id.slice(0, 8).toUpperCase() },
  };
});

export const attachMockRegistry = <T extends { meta?: TileMeta | null }>(
  id: string,
  result: T,
): T => {
  const registry = MOCK_REGISTRY_META[id];
  if (!registry) return result;

  return {
    ...result,
    meta: {
      ...result.meta,
      registry: result.meta?.registry ?? registry,
    },
  };
};

export const attachMockRegistryToMetrics = (
  mocks: Record<string, MetricResult>,
): Record<string, MetricResult> =>
  Object.fromEntries(Object.entries(mocks).map(([id, result]) => [id, attachMockRegistry(id, result)]));

export const attachMockRegistryToInsights = <T extends { meta?: TileMeta | null }>(
  mocks: Record<string, T>,
): Record<string, T> =>
  Object.fromEntries(Object.entries(mocks).map(([id, result]) => [id, attachMockRegistry(id, result)]));
